import { useState } from 'react';
import { MediaURL } from '../../template/imageCarouselTypes';
import BackendService from '../../../services/admin/BackendService';
import { newContentId } from '../../../services/TemplateUtils';

/** The picked file's displayed pixel size, saved with the image; empty if the browser can't decode it. */
export async function readImagePixelSize(
  file: File,
): Promise<{ width: number; height: number } | Record<string, never>> {
  if (typeof createImageBitmap !== 'function') return {};
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size.width && size.height ? size : {};
  } catch {
    return {};
  }
}

/**
 * Uploads an image file onto the content model and hands back its url with
 * its pixel size, so the hero image can be laid out before it loads. Non-image
 * files are ignored.
 */
export function useHeroImageUpload({
  modelId,
  modelType,
  backendService,
  onUploaded,
}: {
  modelId: string;
  modelType: string;
  backendService: BackendService;
  onUploaded: (imageUrl: MediaURL) => void;
}) {
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file: File | undefined) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const size = await readImagePixelSize(file);
      const url = await backendService.uploadAssetContent(
        'images',
        modelId,
        modelType,
        file.name,
        file,
        ext,
        true,
      );
      onUploaded({ id: newContentId(), url, ...size });
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  return { uploading, uploadFile };
}
